'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AvaMark } from '~/components/ava-mark';
import { belowFold, motionAllowed, onceInView } from './motion';
import s from './home.module.css';

// The homepage's product examples. Every property, person and number here is
// fictional; the footer says so.

const chipClass = { work: s.work, needs: s.needs, done: s.done } as const;
const chipLabel = { work: 'Working', needs: 'Needs you', done: 'Done' } as const;

const feed = [
  {
    title: 'Reply to DBI about the rear stair complaint',
    chip: 'work',
    meta: 'Larkspur Court · matched to a 2019 permit',
    status: 'Drafting the reply to DBI for Larkspur Court.',
  },
  {
    title: 'Annual backflow test, due Dec 3',
    chip: 'work',
    meta: 'The Juniper · request drafted to your vendor',
    status: 'Asking your backflow vendor for a test date at The Juniper.',
  },
  {
    title: 'File the window replacement permit',
    chip: 'needs',
    meta: 'Alder House · one approval and it goes',
    status: 'Waiting on your OK to file the permit for Alder House.',
  },
  {
    title: 'Second property tax installment',
    chip: 'done',
    meta: 'Cypress Flats · receipt saved to the property',
    status: 'Saved the paid tax receipt to Cypress Flats.',
  },
] as const;

/** The hero: Ava moves through the portfolio in step with the Working wave. */
export function HeroDesk() {
  const [active, setActive] = useState(0);
  const [status, setStatus] = useState<string>(feed[0].status);
  const [swapping, setSwapping] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const desk = ref.current;
    if (!desk || !motionAllowed()) return;
    let index = 0;
    let swap: number | undefined;
    const cycle = window.setInterval(() => {
      // Hold still while someone is reading or tabbing through it.
      if (desk.matches(':hover, :focus-within')) return;
      index = (index + 1) % feed.length;
      setActive(index);
      setSwapping(true);
      swap = window.setTimeout(() => {
        setStatus(feed[index]!.status);
        setSwapping(false);
      }, 350);
    }, 3600);
    return () => {
      clearInterval(cycle);
      clearTimeout(swap);
    };
  }, []);
  return (
    <div
      ref={ref}
      className={s.desk}
      role="group"
      aria-label="Product example: what Ava is working on across a portfolio"
      data-test="hero-activity"
    >
      <div className={s.deskHead}>
        {/* The one looping mark on the page, next to its status line. */}
        <AvaMark state="working" size={76} animate />
        <div>
          <div className={s.who}>Ava · Working</div>
          <div className={s.status} data-swap={swapping ? '' : undefined}>
            {status}
          </div>
        </div>
      </div>
      <ul className={s.feed}>
        {feed.map((item, index) => (
          <li key={item.title} data-on={index === active ? '' : undefined}>
            <span className={s.feedTitle}>{item.title}</span>
            <span className={`${s.chip} ${chipClass[item.chip]}`}>{chipLabel[item.chip]}</span>
            <span className={s.feedMeta}>{item.meta}</span>
          </li>
        ))}
      </ul>
      <div className={s.deskFoot}>
        <span>
          Your portfolio · Ava is watching <b>77 properties</b>
        </span>
        <span>
          <b>3</b> need you today
        </span>
      </div>
    </div>
  );
}

/** How a complaint becomes a fine, and where Ava steps in. The line fills as you scroll. */
export function EarlyChain() {
  const ref = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const chain = ref.current;
    if (!chain || !motionAllowed() || !belowFold(chain)) return;
    chain.dataset.armed = '';
    const steps = Array.from(chain.children) as HTMLElement[];
    let frame = 0;
    const update = () => {
      frame = 0;
      const box = chain.getBoundingClientRect();
      const line = window.innerHeight * 0.62;
      const progress = Math.max(0, Math.min(1, (line - box.top) / box.height));
      chain.style.setProperty('--p', progress.toFixed(3));
      for (const step of steps)
        if (step.getBoundingClientRect().top + 20 < line) step.dataset.lit = '';
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const check = (
    <span className={s.node}>
      <span className={s.check}>✓</span>
    </span>
  );
  return (
    <ol
      ref={ref}
      className={s.chain}
      aria-label="How a complaint becomes a fine, and where Ava steps in"
    >
      <li className={s.caught}>
        <span className={s.node}>
          <AvaMark state="watching" size={26} />
        </span>
        <div className={s.step}>
          <div className={s.when}>Oct 6 · 8:12 AM</div>
          <div className={s.stepTitle}>Health complaint filed: mold in a unit bathroom</div>
          <div className={s.stepBody}>
            Maple Terrace. Caught that morning, before any inspector was assigned.
          </div>
        </div>
      </li>
      <li className={s.fixed}>
        {check}
        <div className={s.step}>
          <div className={s.when}>Oct 8</div>
          <div className={s.stepTitle}>Remediation vendor booked, tenant notified</div>
          <div className={s.stepBody}>
            You approved the quote. Notice of entry sent to the tenant.
          </div>
        </div>
      </li>
      <li className={s.fixed}>
        {check}
        <div className={s.step}>
          <div className={s.when}>Oct 20</div>
          <div className={s.stepTitle}>Inspection passed</div>
          <div className={s.stepBody}>Photos, invoice, and result saved to the property.</div>
        </div>
      </li>
      <li className={s.avoided}>
        <span className={s.node} />
        <div className={s.step}>
          <div className={s.stepTitle}>Notice of violation</div>
          <div className={s.stepBody}>Never issued.</div>
        </div>
      </li>
      <li className={s.avoided}>
        <span className={s.node} />
        <div className={s.step}>
          <div className={s.stepTitle}>Penalties and a reinspection fee</div>
          <div className={s.stepBody}>Never billed to the owner.</div>
        </div>
      </li>
    </ol>
  );
}

/** One alert, one decision, one receipt: Approve gets pressed, Needs you pings, the check draws. */
export function Flow() {
  const ref = useRef<HTMLDivElement>(null);
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(3);
  const [pressed, setPressed] = useState(false);
  useEffect(() => {
    const flow = ref.current;
    if (!flow || !motionAllowed() || !belowFold(flow)) return;
    setArmed(true);
    setShown(0);
    const timers: number[] = [];
    const at = (ms: number, run: () => void) => timers.push(window.setTimeout(run, ms));
    const stop = onceInView(flow, () => {
      at(0, () => setShown(1));
      at(700, () => setShown(2));
      at(2000, () => setPressed(true));
      at(2450, () => setPressed(false));
      at(2500, () => setShown(3));
    });
    return () => {
      stop();
      timers.forEach(clearTimeout);
    };
  }, []);
  const card = (index: number) => ({
    className: s.msg,
    'data-in': shown > index ? '' : undefined,
  });
  const connector = (
    <span className={s.conn} aria-hidden="true">
      <i />
    </span>
  );
  return (
    <div ref={ref} className={s.flow} data-armed={armed ? '' : undefined}>
      <article {...card(0)}>
        <div className={s.msgTop}>
          <span className={s.msgWho}>
            <AvaMark state="watching" size={22} />
            Ava found something
          </span>
          <span className={`${s.chip} ${s.found}`}>New</span>
        </div>
        <h3>Fire inspection at Hawthorne Flats: two items to fix.</h3>
        <p>
          SFFD flagged two fire extinguishers past their service date and a blocked exit sign.
          Reinspection is set for Nov 18.
        </p>
        <div className={s.msgFoot}>
          Sources: <span>SFFD inspection record</span>, <span>last service tags</span>
        </div>
      </article>
      <article {...card(1)}>
        {connector}
        <div className={s.msgTop}>
          <span className={s.msgWho}>
            <AvaMark state="needs" size={22} play={armed && shown > 1} />
            Ava needs you
          </span>
          <span className={`${s.chip} ${s.needs}`}>Needs you</span>
        </div>
        <h3>Your fire vendor can come Thursday for $186.</h3>
        <p>
          That covers both extinguishers and the exit sign. Approve it and I’ll book the visit and
          let the site manager know.
        </p>
        <div className={s.acts} aria-hidden="true">
          <span data-press={pressed ? '' : undefined}>Approve</span>
          <span>Edit</span>
          <span>Not now</span>
        </div>
      </article>
      <article {...card(2)}>
        {connector}
        <div className={s.msgTop}>
          <span className={s.msgWho}>
            <AvaMark state="done" size={22} play={armed && shown > 2} />
            Ava finished
          </span>
          <span className={`${s.chip} ${s.done}`}>
            <span className={s.check}>✓</span> Done
          </span>
        </div>
        <h3>Fixed Thursday at 2:40 PM.</h3>
        <p>
          New service tags and the exit sign are photographed and saved to Hawthorne Flats, with the
          invoice. The item stays open until the Nov 18 reinspection.
        </p>
        <div className={s.msgFoot}>
          Proof: <span>3 photos</span>, <span>vendor invoice</span>
        </div>
      </article>
    </div>
  );
}

type Job = {
  tab: string;
  from: string;
  title: string;
  sources: readonly (readonly [name: string, got: string, doing: string])[];
  checks: readonly (readonly [kind: 'ok' | 'fix', body: ReactNode])[];
  formLabel: string;
  fields: readonly (readonly [label: string, value: string, source: string])[];
};
const jobs: readonly Job[] = [
  {
    tab: 'Rent Board filing',
    from: 'Assignment from Dana',
    title: 'File the Rent Board housing inventory for Birch Commons. Due Mar 1.',
    sources: [
      [
        'Property management system',
        '24 units, current rents, move-in dates',
        'Reading the property management system',
      ],
      ['Last year’s filing', 'Unit numbers and bedroom counts', 'Pulling last year’s filing'],
      ['Lease files', '3 new tenancies since last March', 'Reading the lease files'],
      ['County assessor', 'Parcel and owner of record', 'Checking the county assessor'],
      ['Rent Board account', 'Filing history and confirmations', 'Opening the Rent Board account'],
    ],
    checks: [
      ['ok', 'Unit count matches the assessor and last year’s filing.'],
      [
        'fix',
        <>
          Occupied units: the PMS said <s>21</s>, a lease file showed a move-in on Oct 3. Corrected
          to <b>22</b>.
        </>,
      ],
      ['ok', 'Owner name matches the recorded deed.'],
    ],
    formLabel: 'Housing inventory, filled in',
    fields: [
      ['Units on the parcel', '24', 'PMS + assessor'],
      ['Occupied units', '22', 'PMS + lease files'],
      ['Tenancies started this year', '3', 'lease files'],
      ['Owner of record', 'Birch Commons LLC', 'assessor'],
    ],
  },
  {
    tab: 'Lender insurance proof',
    from: 'Assignment from Marco',
    title: 'Send proof of insurance to the lender for Maple Terrace.',
    sources: [
      ['Lender’s request', 'Coverage the loan requires', 'Reading the lender’s request'],
      ['Insurance policy', 'Carrier, limits, and policy dates', 'Reading the insurance policy'],
      [
        'Property management system',
        'Owner entity and loan number',
        'Checking the property management system',
      ],
      ['Broker email thread', 'Who to ask for certificates', 'Finding the broker'],
    ],
    checks: [
      ['ok', 'Liability limit of $2M meets the lender’s $1M minimum.'],
      ['ok', 'Policy dates cover the full loan year.'],
      [
        'fix',
        'The property manager is not listed as additional insured. Updated certificate requested from the broker.',
      ],
    ],
    formLabel: 'Lender package, filled in',
    fields: [
      ['Named insured', 'Maple Terrace LLC', 'policy + PMS'],
      ['Liability limit', '$2,000,000', 'policy'],
      ['Policy period', 'Jan 1 to Dec 31', 'policy'],
      ['PM as additional insured', 'Requested', 'broker email'],
    ],
  },
  {
    tab: 'New vendor setup',
    from: 'Assignment from Dana',
    title: 'Set up the new plumbing vendor for Hawthorne Flats.',
    sources: [
      ['Vendor’s email', 'W-9, quote, and contact', 'Reading the vendor’s email'],
      ['State license board', 'License number and status', 'Looking up the contractor license'],
      ['Insurance certificate', 'Coverage and expiration', 'Reading the insurance certificate'],
      ['Property management system', 'Existing vendor list', 'Checking for duplicates in the PMS'],
    ],
    checks: [
      ['ok', 'Contractor license is active and matches the business name.'],
      ['fix', 'Certificate expires in 12 days. Renewal requested from the vendor.'],
      ['ok', 'Not already in the vendor list. No duplicate created.'],
    ],
    formLabel: 'Vendor record, filled in',
    fields: [
      ['Business name', 'Bayline Plumbing Co.', 'W-9 + license'],
      ['License status', 'Active', 'state license board'],
      ['Insurance expires', 'Renewal requested', 'certificate'],
      ['Add to PMS', 'Ready for your OK', 'vendor list'],
    ],
  },
];

type Progress = {
  sources: number;
  checks: number;
  fields: number;
  live: number | null;
  done: boolean;
  status: string;
};
const finished = (job: Job, status = 'Ready for your review.'): Progress => ({
  sources: job.sources.length,
  checks: job.checks.length,
  fields: job.fields.length,
  live: null,
  done: true,
  status,
});

/** Give Ava the assignment: watch Ava gather the context, cross-check it and fill in the form. */
export function AssignmentDemo() {
  const ref = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);
  const [index, setIndex] = useState(0);
  const current = useRef(0);
  const [run, setRun] = useState(0);
  const [progress, setProgress] = useState<Progress>(() => finished(jobs[0]!));
  const job = jobs[index]!;

  const autoplay = useRef<(() => void) | undefined>(undefined);
  const play = useCallback((next: number) => {
    autoplay.current?.();
    timers.current.forEach(clearTimeout);
    timers.current = [];
    current.current = next;
    setIndex(next);
    const job = jobs[next]!;
    if (!motionAllowed()) return setProgress(finished(job));
    setRun((value) => value + 1);
    setProgress({ sources: 0, checks: 0, fields: 0, live: null, done: false, status: '' });
    const at = (ms: number, update: (current: Progress) => Progress) =>
      timers.current.push(window.setTimeout(() => setProgress(update), ms));
    let t = 200;
    job.sources.forEach(([, , doing], k) => {
      at(t, (p) => ({ ...p, status: `${doing}…` }));
      at(t + 420, (p) => ({ ...p, sources: k + 1 }));
      t += 620;
    });
    job.checks.forEach((_, k) => {
      at(t, (p) => ({ ...p, status: 'Cross-checking the sources…' }));
      at(t + 380, (p) => ({ ...p, checks: k + 1 }));
      t += 700;
    });
    job.fields.forEach(([label], k) => {
      at(t, (p) => ({ ...p, live: k, status: `Filling in ${label.toLowerCase()}…` }));
      at(t + 300, (p) => ({ ...p, fields: k + 1 }));
      at(t + 700, (p) => ({ ...p, live: p.live === k ? null : p.live }));
      t += 480;
    });
    at(t + 200, () => finished(job, 'Gathered, verified, and filled in. Ready for your review.'));
  }, []);

  useEffect(() => {
    const demo = ref.current;
    // Plays once on arrival, but never replays content already on screen at load.
    if (demo && motionAllowed() && belowFold(demo))
      autoplay.current = onceInView(demo, () => play(current.current), '0px 0px -25% 0px');
    return () => {
      autoplay.current?.();
      timers.current.forEach(clearTimeout);
    };
  }, [play]);

  const pending = (shown: boolean) => (shown ? undefined : '');
  return (
    <div
      ref={ref}
      className={s.job}
      role="group"
      aria-label="Interactive product example: Ava gathering context, verifying it, and completing paperwork"
    >
      <div className={s.tabs} role="group" aria-label="Pick an assignment">
        {jobs.map((item, k) => (
          <button key={item.tab} type="button" aria-pressed={k === index} onClick={() => play(k)}>
            {item.tab}
          </button>
        ))}
      </div>
      <div className={s.jobHead}>
        {progress.done ? (
          <AvaMark key={run} state="needs" size={40} play={run > 0} />
        ) : (
          <AvaMark state="working" size={40} />
        )}
        <div className="min-w-0">
          <div className={s.jobFrom}>{job.from}</div>
          <div className={s.jobTitle}>{job.title}</div>
          <div className={s.jobStatus}>{progress.status}</div>
        </div>
      </div>
      <div className={s.jobSec}>
        <div className={s.lab}>Context Ava gathered</div>
        <ul className={s.srcs}>
          {job.sources.map(([name, got], k) => (
            <li key={name} data-pend={pending(k < progress.sources)}>
              <span className={s.check}>✓</span>
              <span>{name}</span>
              <span>{got}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={s.jobSec}>
        <div className={s.lab}>Checked against each other</div>
        <ul className={s.verify}>
          {job.checks.map(([kind, body], k) => (
            <li key={k} data-pend={pending(k < progress.checks)}>
              <span className={s.vt} data-fix={kind === 'fix' ? '' : undefined}>
                {kind === 'fix' ? 'Caught' : '✓ Verified'}
              </span>
              <span>{body}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={s.jobSec}>
        <div className={s.lab}>{job.formLabel}</div>
        <div className={s.fields}>
          {job.fields.map(([label, value, source], k) => (
            <div
              key={label}
              className={s.fld}
              data-pend={pending(k < progress.fields)}
              data-live={progress.live === k ? '' : undefined}
            >
              <div className={s.fl}>{label}</div>
              <div className={s.fv}>{value}</div>
              <div className={s.fs}>from {source} · verified</div>
            </div>
          ))}
        </div>
      </div>
      <div className={s.jobFoot} data-pend={pending(progress.done)}>
        <span className={`${s.chip} ${s.needs}`}>Ready for your review</span>
        <span>
          {job.sources.length} sources · every field traced
          <button type="button" className={s.replay} onClick={() => play(index)}>
            Watch again
          </button>
        </span>
      </div>
    </div>
  );
}

const paperwork = [
  {
    title: 'Notice of entry',
    meta: 'Maple Terrace · unit 3 · 24 hours',
    lines: ['100%', '100%', '60%'],
    stamp: 'Sent to tenant · copy saved',
  },
  {
    title: 'Vendor invoice',
    meta: 'Hawthorne Flats · fire vendor · $186',
    lines: ['100%', '75%', '50%'],
    stamp: 'Matched to the job · approved',
  },
  {
    title: 'Utility account transfer',
    meta: 'Birch Commons · water and garbage',
    lines: ['85%', '100%', '40%'],
    stamp: 'Transferred · confirmation saved',
  },
  {
    title: 'Monthly owner report',
    meta: 'Maple Terrace · October',
    lines: ['100%', '65%', '55%'],
    stamp: 'Sent to owner · with receipts',
  },
] as const;

/** Paperwork gets done one document at a time while it is on screen. */
export function PaperPile() {
  const ref = useRef<HTMLDivElement>(null);
  const [order, setOrder] = useState<number[]>(() => paperwork.map((_, k) => k));
  const [out, setOut] = useState(false);
  const [done, setDone] = useState(true);
  const [count, setCount] = useState(14);
  useEffect(() => {
    const stack = ref.current;
    if (!stack || !motionAllowed()) return;
    let cycle: number | undefined;
    const timers: number[] = [];
    const step = () => {
      setOut(true);
      timers.push(
        window.setTimeout(() => {
          setOut(false);
          setDone(false);
          setOrder((current) => [...current.slice(1), current[0]!]);
          setCount((value) => value + 1);
          timers.push(window.setTimeout(() => setDone(true), 900));
        }, 700),
      );
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && cycle === undefined) cycle = window.setInterval(step, 3200);
      else if (!entry?.isIntersecting && cycle !== undefined) {
        clearInterval(cycle);
        cycle = undefined;
      }
    });
    observer.observe(stack);
    return () => {
      observer.disconnect();
      clearInterval(cycle);
      timers.forEach(clearTimeout);
    };
  }, []);
  return (
    <div className={s.pile} role="group" aria-label="Product example: paperwork being completed">
      <div className={s.pileHead}>
        <span>This week</span>
        <span>
          <b>{count}</b> done
        </span>
      </div>
      <div ref={ref} className={s.stack}>
        {paperwork.map((doc, k) => {
          const position = order.indexOf(k);
          return (
            <div
              key={doc.title}
              className={s.doc}
              data-pos={position}
              data-out={position === 0 && out ? '' : undefined}
              data-done={position === 0 && done ? '' : undefined}
              aria-hidden={position === 0 ? undefined : true}
            >
              <div className={s.docTitle}>{doc.title}</div>
              <div className={s.docMeta}>{doc.meta}</div>
              <div className={s.lines}>
                {doc.lines.map((width, line) => (
                  <i key={line} style={{ width }} />
                ))}
              </div>
              <div className={s.stamp}>
                <span className={s.check}>✓</span>
                {doc.stamp}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The AddAva wordmark sketched as building strokes (Brand Identity, Gap Center,
// Lockup, addava-wordmark-construction.svg).
const skyline = [
  'M118.1 582.0 L118.1 501.9',
  'M112.0 512.3 L211.1 457.6',
  'M198.9 457.6 L298.0 512.3',
  'M291.9 501.9 L291.9 582.0',
  'M303.5 582.0 L303.5 485.7',
  'M296.5 492.7 L398.9 492.7',
  'M391.9 485.7 L391.9 582.0',
  'M399.5 582.0 L399.5 454.0',
  'M392.5 461.0 L434.6 461.0',
  'M427.6 454.0 L427.6 582.0',
  'M462.8 582.0 L462.8 485.7',
  'M455.8 492.7 L558.1 492.7',
  'M551.1 485.7 L551.1 582.0',
  'M558.7 582.0 L558.7 454.0',
  'M551.7 461.0 L593.8 461.0',
  'M586.8 454.0 L586.8 582.0',
  'M612.3 582.0 L612.3 501.9',
  'M606.2 512.3 L705.3 457.6',
  'M693.1 457.6 L792.2 512.3',
  'M786.1 501.9 L786.1 582.0',
  'M798.0 582.0 L798.0 487.4',
  'M791.0 494.4 L933.6 494.4',
  'M926.6 487.4 L926.6 582.0',
  'M939.3 582.0 L939.3 485.7',
  'M932.3 492.7 L1063.9 492.7',
  'M1056.9 485.7 L1056.9 582.0',
];

/** The wordmark rises like buildings, drawn stroke by stroke once the visitor reaches the bottom. */
export function Skyline({ trigger = 'scroll' }: { trigger?: 'scroll' | 'mount' }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const sky = ref.current;
    if (!sky || !motionAllowed()) return;
    const strokes = Array.from(sky.querySelectorAll<SVGGeometryElement>('[data-stroke]'));
    for (const stroke of strokes)
      stroke.style.setProperty('--len', `${Math.ceil(stroke.getTotalLength())}px`);
    sky.dataset.armed = '';
    const pen = sky.querySelectorAll<SVGCircleElement>('[data-pen]');
    let frame = 0;
    let timer: number | undefined;
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const draw = (k: number) => {
      if (k >= strokes.length) {
        delete sky.dataset.drawing;
        timer = window.setTimeout(() => (sky.dataset.lit = ''), 250);
        return;
      }
      const stroke = strokes[k]!;
      const length = stroke.getTotalLength();
      const duration = k === 0 ? 650 : 60 + length * 0.7;
      let start: number | null = null;
      const tick = (now: number) => {
        start ??= now;
        const t = Math.min(1, (now - start) / duration);
        const point = stroke.getPointAtLength(length * ease(t));
        stroke.style.strokeDashoffset = `${length * (1 - ease(t))}px`;
        pen.forEach((dot) => {
          dot.setAttribute('cx', String(point.x));
          dot.setAttribute('cy', String(point.y));
        });
        if (t < 1) frame = requestAnimationFrame(tick);
        else timer = window.setTimeout(() => draw(k + 1), k === 0 ? 120 : 35);
      };
      frame = requestAnimationFrame(tick);
    };
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      sky.dataset.drawing = '';
      draw(0);
    };
    // Visibility, not reaching the document bottom: short pages and restored
    // scroll positions may already show the footer before any scroll event.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        start();
      },
      { threshold: 0.1 },
    );
    if (trigger === 'mount') start();
    else observer.observe(sky);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [trigger]);
  return (
    <svg
      ref={ref}
      className={s.skyline}
      viewBox="80 440 1010 150"
      role="img"
      aria-label="The AddAva wordmark sketched as a skyline"
    >
      <line className={s.ground} data-stroke="" x1="80" y1="575" x2="1090" y2="575" />
      {skyline.map((d) => (
        <path key={d} className={s.stroke} data-stroke="" d={d} />
      ))}
      <circle className={s.lampGlow} cx="413.5" cy="478" r="16" />
      <circle className={s.lamp} cx="413.5" cy="478" r="5" />
      <circle className={s.penHalo} data-pen="" cx="80" cy="575" r="9" />
      <circle className={s.pen} data-pen="" cx="80" cy="575" r="3" />
    </svg>
  );
}
